import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { selectFuelCosts, selectCommunityData } from "store/community/selectors";
import { updateFuelCosts } from "store/community/actions";

import { getBaseUnits } from "utils/fields";

import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import classes from "./style.module.scss";

const FuelPrices = ({ modelUnits, fuelCosts, updateFuelCosts, community }) => {
    const [initialLoad, setInitialLoad] = useState(true);

    const { communityDetails: { createdAt: { seconds: secondsCreated = 0 } = {} } = {} } = community || {};

    const dateCreated = new Date(secondsCreated * 1000);
    const year = dateCreated.getFullYear();
    const month = dateCreated.getMonth() + 1;

    const {
        electricity: {
            dollarsPerUnit: elecVariableRate = 0,
            dollarsPerMonth: elecFixedMonthlyRate = 0,
            preTaxRebate: elecPreTaxRebate = 0,
            tax: elecTax = 0,
        } = {},
        naturalGas: {
            dollarsPerUnit: natGasVariableRate = 0,
            dollarsPerMonth: natGasFixedMonthlyRate = 0,
            preTaxRebate: natGasPreTaxRebate = 0,
            tax: natGasTax = 0,
        } = {},
        propane: {
            dollarsPerUnit: propaneVariableRate = 0,
            dollarsPerMonth: propaneFixedMonthlyRate = 0,
            preTaxRebate: propanePreTaxRebate = 0,
            tax: propaneTax = 0,
        } = {},
        oil: {
            dollarsPerUnit: oilVariableRate = 0,
            dollarsPerMonth: oilFixedMonthlyRate = 0,
            preTaxRebate: oilPreTaxRebate = 0,
            tax: oilTax = 0,
        } = {},
        wood: {
            dollarsPerUnit: woodVariableRate = 0,
            dollarsPerMonth: woodFixedMonthlyRate = 0,
            preTaxRebate: woodPreTaxRebate = 0,
            tax: woodTax = 0,
        } = {},
    } = fuelCosts || {};

    const handleUpdateFuelCosts = (fuelType, name, value) => {
        if (initialLoad) return;

        updateFuelCosts(fuelType, name, value);
    };

    useEffect(() => {
        if (initialLoad) {
            setInitialLoad(false);
        }
    }, [initialLoad]);

    return (
        <div className={classes.fuelPricesContainer}>
            <h4 style={{ marginBottom: "1rem" }}>Fuel Prices</h4>
            <>
                <InputRow gridTemplate="1fr 1fr">
                    <div>
                        <h5 style={{ marginBottom: "0.5rem", marginTop: "0.5rem" }}>Electricity</h5>
                        <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                            <InputWithUnits
                                type="number"
                                name={`elecVariableRate`}
                                label="Variable Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/kWh"],
                                    selected: "$/kWh",
                                    unitType: "price",
                                }}
                                input={{
                                    value: elecVariableRate,
                                    onChange: (value) => handleUpdateFuelCosts("electricity", "dollarsPerUnit", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`elecFixedMonthlyRate`}
                                label="Fixed Monthly Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/mo"],
                                    selected: "$/mo",
                                    unitType: "price",
                                }}
                                input={{
                                    value: elecFixedMonthlyRate,
                                    onChange: (value) => handleUpdateFuelCosts("electricity", "dollarsPerMonth", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`elecPreTaxRebate`}
                                label="Rebate (Before Tax)"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: elecPreTaxRebate,
                                    onChange: (value) => handleUpdateFuelCosts("electricity", "preTaxRebate", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`elecTax`}
                                label="Tax"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: elecTax,
                                    onChange: (value) => handleUpdateFuelCosts("electricity", "tax", value),
                                }}
                            />
                        </InputRow>
                    </div>
                    <div>
                        <h5 style={{ marginBottom: "0.5rem", marginTop: "0.5rem" }}>Natural Gas</h5>
                        <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                            <InputWithUnits
                                type="number"
                                name={`natGasVariableRate`}
                                label="Variable Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/m³"],
                                    selected: "$/m³",
                                    unitType: "price",
                                }}
                                input={{
                                    value: natGasVariableRate,
                                    onChange: (value) => handleUpdateFuelCosts("naturalGas", "dollarsPerUnit", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`natGasFixedMonthlyRate`}
                                label="Fixed Monthly Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/mo"],
                                    selected: "$/mo",
                                    unitType: "price",
                                }}
                                input={{
                                    value: natGasFixedMonthlyRate,
                                    onChange: (value) => handleUpdateFuelCosts("naturalGas", "dollarsPerMonth", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`natGasPreTaxRebate`}
                                label="Rebate (Before Tax)"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: natGasPreTaxRebate,
                                    onChange: (value) => handleUpdateFuelCosts("naturalGas", "preTaxRebate", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`natGasTax`}
                                label="Tax"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: natGasTax,
                                    onChange: (value) => handleUpdateFuelCosts("naturalGas", "tax", value),
                                }}
                            />
                        </InputRow>
                    </div>
                </InputRow>
                <InputRow gridTemplate="1fr 1fr">
                    <div>
                        <h5 style={{ marginBottom: "0.5rem" }}>Propane</h5>
                        <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                            <InputWithUnits
                                type="number"
                                name={`propaneVariableRate`}
                                label="Variable Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/L"],
                                    selected: "$/L",
                                    unitType: "price",
                                }}
                                input={{
                                    value: propaneVariableRate,
                                    onChange: (value) => handleUpdateFuelCosts("propane", "dollarsPerUnit", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`propaneFixedMonthlyRate`}
                                label="Fixed Monthly Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/mo"],
                                    selected: "$/mo",
                                    unitType: "price",
                                }}
                                input={{
                                    value: propaneFixedMonthlyRate,
                                    onChange: (value) => handleUpdateFuelCosts("propane", "dollarsPerMonth", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`propanePreTaxRebate`}
                                label="Rebate (Before Tax)"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: propanePreTaxRebate,
                                    onChange: (value) => handleUpdateFuelCosts("propane", "preTaxRebate", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`propaneTax`}
                                label="Tax"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: propaneTax,
                                    onChange: (value) => handleUpdateFuelCosts("propane", "tax", value),
                                }}
                            />
                        </InputRow>
                    </div>
                    <div>
                        <h5 style={{ marginBottom: "0.5rem" }}>Oil</h5>
                        <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                            <InputWithUnits
                                type="number"
                                name={`oilVariableRate`}
                                label="Variable Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/L"],
                                    selected: "$/L",
                                    unitType: "price",
                                }}
                                input={{
                                    value: oilVariableRate,
                                    onChange: (value) => handleUpdateFuelCosts("oil", "dollarsPerUnit", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`oilFixedMonthlyRate`}
                                label="Fixed Monthly Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/mo"],
                                    selected: "$/mo",
                                    unitType: "price",
                                }}
                                input={{
                                    value: oilFixedMonthlyRate,
                                    onChange: (value) => handleUpdateFuelCosts("oil", "dollarsPerMonth", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`oilPreTaxRebate`}
                                label="Rebate (Before Tax)"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: oilPreTaxRebate,
                                    onChange: (value) => handleUpdateFuelCosts("oil", "preTaxRebate", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`oilTax`}
                                label="Tax"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: oilTax,
                                    onChange: (value) => handleUpdateFuelCosts("oil", "tax", value),
                                }}
                            />
                        </InputRow>
                    </div>
                </InputRow>
                <InputRow gridTemplate="1fr 1fr">
                    <div>
                        <h5 style={{ marginBottom: "0.5rem" }}>Wood</h5>
                        <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                            <InputWithUnits
                                type="number"
                                name={`woodVariableRate`}
                                label="Variable Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/cord"],
                                    selected: "$/cord",
                                    unitType: "price",
                                }}
                                input={{
                                    value: woodVariableRate,
                                    onChange: (value) => handleUpdateFuelCosts("wood", "dollarsPerUnit", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`woodFixedMonthlyRate`}
                                label="Fixed Monthly Rate"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelPrice", modelUnits),
                                    options: ["$/mo"],
                                    selected: "$/mo",
                                    unitType: "price",
                                }}
                                input={{
                                    value: woodFixedMonthlyRate,
                                    onChange: (value) => handleUpdateFuelCosts("wood", "dollarsPerMonth", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`woodPreTaxRebate`}
                                label="Rebate (Before Tax)"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: woodPreTaxRebate,
                                    onChange: (value) => handleUpdateFuelCosts("wood", "preTaxRebate", value),
                                }}
                            />
                            <InputWithUnits
                                type="number"
                                name={`woodTax`}
                                label="Tax"
                                placeholder="0.00"
                                decimals={2}
                                units={{
                                    base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                    options: ["%"],
                                    selected: "%",
                                    unitType: "fraction",
                                }}
                                input={{
                                    value: woodTax,
                                    onChange: (value) => handleUpdateFuelCosts("wood", "tax", value),
                                }}
                            />
                        </InputRow>
                    </div>
                </InputRow>
            </>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    fuelCosts: selectFuelCosts,
    community: selectCommunityData,
});

const mapDispatchToProps = (dispatch) => ({
    updateFuelCosts: (fuelType, name, value) => dispatch(updateFuelCosts(fuelType, name, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FuelPrices);
